import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import http from "../services/http";
import "../scss/login.scss";

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const token = searchParams.get("token") || "";
    const email = searchParams.get("email") || "";

    const [formData, setFormData] = useState({
        new_password: "",
        confirm_password: "",
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (!token || !email) {
            navigate("/forgot-password");
        }
    }, [token, email, navigate]);

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.new_password) {
            newErrors.new_password = "Password baru harus diisi";
        } else if (formData.new_password.length < 8) {
            newErrors.new_password = "Password minimal 8 karakter";
        }

        if (!formData.confirm_password) {
            newErrors.confirm_password = "Konfirmasi password harus diisi";
        } else if (formData.new_password !== formData.confirm_password) {
            newErrors.confirm_password = "Konfirmasi password tidak cocok";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setApiError("");

        try {
            await http.post("auth/reset-password", {
                email,
                token,
                new_password: formData.new_password,
            });
            setSuccessMessage(
                "Password berhasil diperbarui. Anda akan diarahkan ke halaman login..."
            );
            setTimeout(() => navigate("/login"), 3000);
        } catch (error: any) {
            setApiError(
                error.response?.data?.message ||
                "Gagal reset password. Link mungkin sudah kadaluarsa."
            );
        } finally {
            setLoading(false);
        }
    };

    const isFormValid =
        formData.new_password.length >= 8 &&
        formData.confirm_password.length > 0 &&
        formData.new_password === formData.confirm_password &&
        !Object.values(errors).some(err => err);

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>Reset Password</h1>
                    <p>Buat password baru untuk akun Anda</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    {apiError && (
                        <div className="alert alert-error">{apiError}</div>
                    )}

                    {successMessage ? (
                        <div className="alert alert-success">{successMessage}</div>
                    ) : (
                        <>
                            <div className="form-group">
                                <label htmlFor="new_password">Password Baru *</label>
                                <div className="password-wrapper">
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        id="new_password"
                                        name="new_password"
                                        value={formData.new_password}
                                        onChange={handleChange}
                                        placeholder="Minimal 8 karakter"
                                        className={`form-input ${errors.new_password ? "error" : ""}`}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                    >
                                        {showNewPassword ? <FiEyeOff /> : <FiEye />}
                                    </button>
                                </div>
                                {errors.new_password && (
                                    <span className="error-message">{errors.new_password}</span>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirm_password">Konfirmasi Password *</label>
                                <div className="password-wrapper">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        id="confirm_password"
                                        name="confirm_password"
                                        value={formData.confirm_password}
                                        onChange={handleChange}
                                        placeholder="Ulangi password baru"
                                        className={`form-input ${errors.confirm_password ? "error" : ""}`}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                                    </button>
                                </div>
                                {errors.confirm_password && (
                                    <span className="error-message">{errors.confirm_password}</span>
                                )}
                            </div>

                            <button
                                type="submit"
                                className={`submit-button ${isFormValid && !loading ? "active" : "disabled"}`}
                                disabled={!isFormValid || loading}
                            >
                                {loading ? "Menyimpan..." : "Simpan Password Baru"}
                            </button>
                        </>
                    )}

                    <p className="register-link">
                        <a href="/login">← Kembali ke Login</a>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
